
import { useEffect, useRef, useLayoutEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Coins } from "lucide-react";
import { Link } from "react-router-dom";
import { useAppSelector } from "@/lib/hooks";

const Pricing = () => {
  const mountedRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const isAuthenticated = useAppSelector(state => state.user.isAuthenticated);

  useLayoutEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (!mounted) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 mt-16">
          <div className="animate-pulse space-y-8">
            <div className="max-w-3xl mx-auto space-y-4">
              <div className="h-8 bg-muted rounded w-2/3 mx-auto" />
              <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
            </div>
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[600px] bg-muted rounded" />
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const plans = [
    {
      name: "Starter",
      price: "$2",
      tokens: 5,
      perToken: "$0.40/token",
      popular: false,
      features: [
        "5 AI-powered city guides",
        "Access to public guides",
        "Basic customization options",
        "Standard support"
      ]
    },
    {
      name: "Popular",
      price: "$5",
      tokens: 15,
      perToken: "$0.33/token",
      popular: true,
      features: [
        "15 AI-powered city guides",
        "Access to public guides",
        "Advanced customization options",
        "Priority support",
        "Save guides offline"
      ]
    },
    {
      name: "Pro",
      price: "$10",
      tokens: 40,
      perToken: "$0.25/token",
      popular: false,
      features: [
        "40 AI-powered city guides",
        "Access to public guides",
        "Premium customization options",
        "24/7 Priority support",
        "Save guides offline",
        "Collaboration features"
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-16 mt-16">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-4xl font-bold tracking-tight">Simple, Token-Based Pricing</h1>
          <p className="mt-4 text-xl text-muted-foreground">
            Choose the plan that best fits your travel needs. Each token generates one unique AI city guide.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`relative overflow-hidden ${
                plan.popular ? 'border-primary shadow-lg scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1">
                  Most Popular
                </div>
              )}
              <div className="p-6">
                <h3 className="text-2xl font-bold">{plan.name}</h3>
                
                <div className="flex items-center gap-2 my-4">
                  <Coins className="h-6 w-6 text-primary" />
                  <span className="text-3xl font-bold">{plan.tokens}</span>
                  <span className="text-muted-foreground">tokens</span>
                </div>

                <div className="mb-6">
                  <div className="text-3xl font-bold">{plan.price}</div>
                  <div className="text-sm text-muted-foreground">{plan.perToken}</div>
                </div>

                <Link
                  to={isAuthenticated ? `/checkout?plan=${plan.name.toLowerCase()}` : "/login"}
                  className="block w-full mb-6"
                >
                  <Button 
                    className="w-full"
                    variant={plan.popular ? "default" : "outline"}
                  >
                    Get Started
                  </Button>
                </Link>

                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Pricing;
